import React, { useState, useEffect } from 'react';
import { Modal, Button, Card, Space, message, Spin, Row, Col, Typography, Tag, Input } from 'antd';
import { QrcodeOutlined, CopyOutlined, DownloadOutlined, ReloadOutlined } from '@ant-design/icons';
import QRCode from 'qrcode';

const { Text } = Typography;

interface ProjectQRCodeProps {
  project: {
    id: number;
    name: string;
    description?: string;
    qr_code?: string;
  };
  visible: boolean;
  onClose: () => void;
}

const ProjectQRCode: React.FC<ProjectQRCodeProps> = ({ project, visible, onClose }) => {
  const [qrCodeDataUrl, setQrCodeDataUrl] = useState<string>('');
  const [projectUrl, setProjectUrl] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const [qrCodeId, setQrCodeId] = useState<string>('');

  // 获取移动端URL配置
  const getMobileUrl = () => {
    // 线上地址 微信只能线上访问
    const prodUrl = 'http://mobile.ddylcs.org.cn';
    return prodUrl;
  };

  // 生成项目QR码ID（如果没有的话）
  const generateQRCodeId = () => {
    if (project.qr_code) {
      return project.qr_code;
    }
    // 生成一个新的QR码ID：项目ID + 时间戳
    return `proj_${project.id}_${Date.now()}`;
  };

  // 生成QR码
  const generateQRCode = async () => {
    try {
      setLoading(true);
      
      const qrId = generateQRCodeId();
      setQrCodeId(qrId);
      
      const mobileUrl = getMobileUrl();
      const url = `${mobileUrl}/project/${qrId}`;
      setProjectUrl(url);
      
      console.log('生成项目QR码URL:', url);
      
      const qrCodeDataUrl = await QRCode.toDataURL(url, {
        width: 400,
        margin: 2,
        color: {
          dark: '#000000',
          light: '#FFFFFF'
        }
      });

      setQrCodeDataUrl(qrCodeDataUrl);
      message.success('QR码生成成功');
    } catch (error) {
      console.error('生成QR码失败:', error);
      message.error('生成QR码失败');
    } finally {
      setLoading(false);
    }
  };

  // 复制链接
  const copyLink = () => {
    navigator.clipboard.writeText(projectUrl).then(() => {
      message.success('链接已复制到剪贴板');
    }).catch(() => {
      message.error('复制失败');
    });
  };

  // 下载QR码
  const downloadQRCode = () => {
    if (!qrCodeDataUrl) return;

    const filename = `${project.name}-项目二维码.png`;

    const link = document.createElement('a');
    link.download = filename;
    link.href = qrCodeDataUrl;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    
    message.success('QR码已下载');
  };

  // 重置状态
  useEffect(() => {
    if (visible) {
      setQrCodeDataUrl('');
      setProjectUrl('');
      setQrCodeId('');
      
      // 如果项目已有qr_code，自动生成
      if (project.qr_code) {
        generateQRCode();
      }
    }
  }, [visible, project.qr_code]);

  return (
    <Modal
      title={`${project.name} - 项目二维码`}
      open={visible}
      onCancel={onClose}
      width={600}
      footer={[
        <Button key="close" onClick={onClose}>
          关闭
        </Button>
      ]}
    >
      <div style={{ marginBottom: 16 }}>
        <Space direction="vertical" style={{ width: '100%' }}>
          <Text type="secondary">
            扫描下方二维码可直接在手机端打开项目页面，系统将根据项目配置自动处理登录、协议签署等流程
          </Text>
          
          <Space>
            <Button 
              type="primary" 
              icon={<QrcodeOutlined />} 
              onClick={generateQRCode}
              loading={loading}
            >
              {qrCodeDataUrl ? '重新生成' : '生成项目二维码'}
            </Button>
            
            {qrCodeDataUrl && (
              <Button 
                icon={<ReloadOutlined />} 
                onClick={generateQRCode}
                loading={loading}
              >
                刷新
              </Button>
            )}
          </Space>
        </Space>
      </div>

      <Spin spinning={loading}>
        {qrCodeDataUrl ? (
          <Row gutter={16}>
            <Col span={12}>
              <Card title="二维码" size="small">
                <div style={{ textAlign: 'center' }}>
                  <img 
                    src={qrCodeDataUrl} 
                    alt={`${project.name} 项目二维码`}
                    style={{ 
                      width: '100%', 
                      maxWidth: 250, 
                      height: 'auto',
                      marginBottom: 12
                    }}
                  />
                  <div>
                    <Space>
                      <Button 
                        type="primary" 
                        icon={<DownloadOutlined />} 
                        size="small"
                        onClick={downloadQRCode}
                      >
                        下载二维码
                      </Button>
                    </Space>
                  </div>
                </div>
              </Card>
            </Col>
            
            <Col span={12}>
              <Card title="项目信息" size="small">
                <div style={{ marginBottom: 12 }}>
                  <Text strong>项目名称：</Text>
                  <br />
                  <Text>{project.name}</Text>
                </div>
                
                {project.description && (
                  <div style={{ marginBottom: 12 }}>
                    <Text strong>项目描述：</Text>
                    <br />
                    <Text type="secondary">{project.description}</Text>
                  </div>
                )}
                
                <div style={{ marginBottom: 12 }}>
                  <Text strong>二维码ID：</Text>
                  <br />
                  <Text code>{qrCodeId}</Text>
                </div>
                
                <div style={{ marginBottom: 12 }}>
                  <Text strong>访问链接：</Text>
                  <br />
                  <Input.Group compact>
                    <Input 
                      value={projectUrl} 
                      readOnly 
                      style={{ width: 'calc(100% - 80px)' }}
                    />
                    <Button 
                      icon={<CopyOutlined />}
                      onClick={copyLink}
                    >
                      复制
                    </Button>
                  </Input.Group>
                </div>
              </Card>
            </Col>
          </Row>
        ) : (
          <div style={{ textAlign: 'center', padding: '60px 0' }}>
            <QrcodeOutlined style={{ fontSize: 48, color: '#d9d9d9', marginBottom: 16 }} />
            <div>
              <Text type="secondary">点击上方按钮生成项目二维码</Text>
            </div>
          </div>
        )}
      </Spin>
      
      {qrCodeDataUrl && (
        <div style={{ marginTop: 16, padding: 12, background: '#f5f5f5', borderRadius: 6 }}>
          <Text type="secondary" style={{ fontSize: 12 }}>
            <strong>使用说明：</strong><br />
            1. 用户扫描二维码后将进入项目页面<br />
            2. 系统会根据项目配置自动处理登录验证、协议签署等流程<br />
            3. 完成验证后，用户可以看到项目关联的所有问卷并选择填写
          </Text>
        </div>
      )}
    </Modal>
  );
};

export default ProjectQRCode; 